import Vue from 'vue';
import {AUTHENTICATE, NOTIFY} from '../store/mutation-types';
import {delay} from '../utilities';
import {get} from 'lodash';

export const publicRoutes = new Set(['license', 'about', 'login', 'login-with-token']);
// deprecated, change to RS256
const algorithm = 'HS256';
// const algorithm = 'RS256';

export function authBuilder(http, store, config) {
  const {commit, dispatch, cache} = store;
  // entity types
  const FILE = 'FILE';
  const WORKSPACE = 'WORKSPACE';
  // access types
  const CREATE = 'CREATE';

  function parseJwtPayload(token) {
    const {jti, iat, sub, iss, exp} = JSON.parse(atob(token.split('.')[1]));
    return {jti, iat, sub, iss, exp};
  }

  const auth = {
    install(Vue) {
      Vue.prototype.$auth = this;
    },
    isLoggingIn: false,
    isLoggingOut: false,
    async authenticate(token) {
      const {sub: username} = parseJwtPayload(token);
      cache.clear();
      const profile = await dispatch('fetchUserDetails', {silent: true, token});
      const config = await dispatch('fetchUserConfig', {silent: true, token});
      if (!profile || !config) {
        commit(NOTIFY, {message: 'User cannot be authenticated!', variant: 'warning', countDown: 10, clean: true});
        return false;
      }
      commit(AUTHENTICATE, {username, token, profile, config});
      return true;
    },
    async guestLogin() {
      try {
        // just for testing
        await delay(0.5);
        const application = config.database;
        const response = await http.get(`${config.authUrl}/anonym/${application}`, {params: {algorithm}});
        const token = response.data;
        const isAuthenticated = await this.authenticate(token);
        if (isAuthenticated) {
          commit(NOTIFY, {message: 'Logged in successfully as guest!', variant: 'success', countDown: 10, clean: true});
        }
      } catch (error) {
        commit(NOTIFY, {message: 'Sorry, login failed', variant: 'danger', countDown: 30});
        throw error;
      }
    },
    async login(username, password) {
      let error = null;
      try {
        // delay just for testing
        await delay(0.5);
        const application = config.database;
        const {data: token} = await http.post(`${config.authUrl}/login`, {username, password, application, algorithm});
        const isAuthenticated = await this.authenticate(token);
        if (isAuthenticated) {
          commit(NOTIFY, {message: 'User logged in successfully!', variant: 'success', countDown: 10, clean: true});
        }
      } catch (e) {
        console.error(e);
        if (!!e.response && e.response.status === 401) {
          error = 'Sorry, you provided invalid credentials';
        } else if (!!e.response && e.response.status === 403) {
          error = 'Sorry, you have no access to the application';
        } else {
          error = 'Sorry, login failed';
        }
      }
      if (error) {
        commit(NOTIFY, {message: error, variant: 'danger', countDown: 30});
        throw new Error(error);
      }
    },
    async loginWithToken(token) {
      const isAuthenticated = await this.authenticate(token);
      if (isAuthenticated) {
        const isAuthorized = await dispatch('renewToken', {silent: true});
        if (!isAuthorized) throw new Error('Sorry, you have no permissions');
        commit(NOTIFY, {message: 'User logged in successfully!', variant: 'success', countDown: 10, clean: true});
      }
    },
    async logout(router) {
      this.isLoggingOut = true;
      const {name} = router.currentRoute;
      // workaround for direct referencing of license/about pages
      if (!this.isLoggingIn && !publicRoutes.has(name)) {
        await router.push({name: 'login'});
      }
      await dispatch('logout');
      this.isLoggingOut = false;
    },
    getAuthHeaders() {
      return !this.token ? {} : {Authorization: `Bearer ${this.token}`};
    },
    get token() {
      return store.state.auth.token;
    },
    get username() {
      return store.state.auth.username;
    },
    get application() {
      return config.database;
    },
    get profile() {
      return store.state.auth.profile;
    },
    /**
     * Check if user belongs to the group
     *
     * @param group group name
     * @param exclusively whether user belongs only to one specific group or not
     * @param globally whether user belongs globally to the group
     * @return {boolean}
     */
    isMemberOf(group, exclusively = false, globally = false) {
      const groups = get(this, 'profile.groups', []);
      const checkGroup = g => g.group === group && (!globally || g.application === undefined);
      return groups[exclusively ? 'every' : 'some'](checkGroup);
    },
    get isRegistered() {
      return !!this.token && !!this.username && this.username !== 'guest';
    },
    get isAdmin() {
      return !!this.token && this.isMemberOf('Admin');
    },
    get isGlobalAdmin() {
      return !!this.token && this.isMemberOf('Admin', false, true);
    },
    get isGuest() {
      return !!this.token && this.username === 'guest';
    },
    get isLoggedIn() {
      return !!this.token;
    },
    hasPermissions(permissions) {
      const {token, username, application} = this;
      return cache.dispatch('checkUserAccess', {token, permissions: permissions.map(p => ({...p, application, username}))}, {timeout: 180 * 1000});
    },
    // methods to check current user permissions
    async canUploadFiles() {
      if (!this.isLoggedIn || !this.isRegistered) return false;
      if (!config.uploads) return false;
      const [canCreateFile] = await this.hasPermissions([{entityType: FILE, accessType: CREATE}]);
      return canCreateFile;
    },
    async canGenerateSnapshots() { return this.isLoggedIn && this.isAdmin; },
    canEditWorkspace(workspace) {
      if (!workspace || !this.isRegistered || !this.isLoggedIn) return false;
      if (this.isAdmin) return true;
      // TODO: replace with access check call
      return workspace.updatedBy === this.username;
    },
    async canCreateWorkspace() {
      if (!this.isLoggedIn || this.isGuest) return false;
      const [canCreateWorkspace] = await auth.hasPermissions([{entityType: WORKSPACE, accessType: CREATE}]);
      return canCreateWorkspace;
    },
    canSaveWorkspace(currentWorkspace) {
      if (!currentWorkspace) return false;
      if (!this.isRegistered) return false;
      if (!currentWorkspace.updatedBy) return true;
      return this.username === currentWorkspace.updatedBy;
    },
    canDeleteWorkspace(currentWorkspace) {
      if (!currentWorkspace) return false;
      if (!currentWorkspace.updatedBy) return true;
      if (this.username === currentWorkspace.updatedBy) return true;
      return currentWorkspace.publishType !== 'PUBLIC';
    },
    canPublishWorkspace(currentWorkspace, publishType) {
      if (!currentWorkspace) return false;
      if (!this.isLoggedIn) return false;
      if (!this.isRegistered) return false;
      if (this.isAdmin) return true;
      if (this.isGuest && publishType === 'PUBLIC') return false;
      return publishType !== 'PUBLIC';
    },
  };
  Vue.use(auth);

  return auth;
}
